import React, { useEffect, useMemo, useState } from 'react';
import CustomSelect from '../components/CustomSelect';

export default function ClassesView({
  classesWorkspace,
  searchTerm,
  isLoading,
  isSaving,
  saveError,
  onCreateClass,
  onSaveClass,
  onDeleteClass,
  terminology,
  permissions
}) {
  const canWrite = (permissions ?? []).includes('classes.write');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [draft, setDraft] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const projectGradeLevel = classesWorkspace?.project?.targetGrade || classesWorkspace?.project?.sourceGrade || '';

  const filteredClasses = useMemo(() => {
    if (!classesWorkspace) {
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return classesWorkspace.classes;
    }

    return classesWorkspace.classes.filter((schoolClass) => (
      schoolClass.name.toLowerCase().includes(normalizedSearch) ||
      schoolClass.displayName.toLowerCase().includes(normalizedSearch) ||
      schoolClass.teacherName.toLowerCase().includes(normalizedSearch)
    ));
  }, [classesWorkspace, searchTerm]);

  useEffect(() => {
    if (!classesWorkspace) {
      setSelectedClassId('');
      setDraft(null);
      setIsEditDialogOpen(false);
      setIsCreateDialogOpen(false);
      setCreateDraft(null);
    }
  }, [classesWorkspace]);

  useEffect(() => {
    if (!classesWorkspace) {
      setCreateDraft(null);
      return;
    }

    if (isCreateDialogOpen) {
      setCreateDraft(createEmptyDraft(projectGradeLevel));
    }
  }, [classesWorkspace, projectGradeLevel, isCreateDialogOpen]);

  useEffect(() => {
    if (!isCreateDialogOpen && !isEditDialogOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsCreateDialogOpen(false);
        setCreateDraft(createEmptyDraft(projectGradeLevel));
        setIsEditDialogOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projectGradeLevel, isCreateDialogOpen, isEditDialogOpen, isSaving]);

  if (isLoading || !classesWorkspace) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading {terminology.classes.toLowerCase()}</h1>
          <p>Preparing the {terminology.class.toLowerCase()} configuration workspace.</p>
        </div>
      </section>
    );
  }

  const selectedClass = classesWorkspace.classes.find((schoolClass) => schoolClass.id === selectedClassId) ?? null;
  const editorTitle = selectedClass
    ? selectedClass.displayName === selectedClass.name
      ? selectedClass.name
      : `${selectedClass.name} • ${selectedClass.displayName}`
    : `Select a ${terminology.class.toLowerCase()}`;

  async function handleSave(event) {
    event.preventDefault();

    if (!draft || !selectedClass) {
      return;
    }

    const payload = {
      ...draft,
      capacity: Number(draft.capacity)
    };

    await onSaveClass(selectedClass.id, payload);
  }

  function handleDraftChange(field, value) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value
    }));
  }

  function handleCreateDraftChange(field, value) {
    setCreateDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value
    }));
  }

  function handleReset() {
    if (!selectedClass) {
      return;
    }

    setDraft(buildClassDraft(selectedClass));
  }

  function handleCreateClick() {
    if (isSaving) {
      return;
    }

    setIsCreateDialogOpen(true);
  }

  function handleCloseCreateDialog() {
    if (isSaving) {
      return;
    }

    setIsCreateDialogOpen(false);
    setCreateDraft(createEmptyDraft(projectGradeLevel));
  }

  function handleResetCreateDialog() {
    if (isSaving) {
      return;
    }

    setCreateDraft(createEmptyDraft(projectGradeLevel));
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();

    if (!createDraft) {
      return;
    }

    const payload = {
      ...createDraft,
      capacity: Number(createDraft.capacity)
    };
    const updatedWorkspace = await onCreateClass(payload);

    if (!updatedWorkspace) {
      return;
    }

    const createdClass = updatedWorkspace.classes.find(
      (schoolClass) => schoolClass.name === payload.name
    );

    setIsCreateDialogOpen(false);
    setCreateDraft(createEmptyDraft(projectGradeLevel));
    setSelectedClassId(createdClass?.id ?? '');
  }

  function handleOpenDeleteDialog() {
    if (!selectedClass || isSaving) {
      return;
    }
    setDeleteError('');
    setIsDeleteDialogOpen(true);
  }

  function handleCloseDeleteDialog() {
    if (isSaving) {
      return;
    }
    setIsDeleteDialogOpen(false);
    setDeleteError('');
  }

  async function handleConfirmDelete() {
    if (!selectedClass || isSaving) {
      return;
    }

    setDeleteError('');

    try {
      const updatedWorkspace = await onDeleteClass(selectedClass.id);

      if (updatedWorkspace) {
        setIsDeleteDialogOpen(false);
        setIsEditDialogOpen(false);
        setSelectedClassId('');
      }
    } catch (requestError) {
      setDeleteError(requestError.message || `Unable to delete the ${terminology.class.toLowerCase()}.`);
    }
  }

  const lockedStudentsForSelected = selectedClass
    ? (classesWorkspace.lockedStudentsByClass?.[selectedClass.id] ?? [])
    : [];

  return (
    <div className="classes-screen">
      <section className="hero-card roster-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">{terminology.classes}</span>
            <span className="eyebrow-copy">
              {classesWorkspace.summary.totalClasses} {classesWorkspace.summary.totalClasses === 1 ? terminology.class.toLowerCase() : terminology.classes.toLowerCase()} defined
            </span>
          </div>
          <h1>{classesWorkspace.project.name}</h1>
          <p>
            Define the {terminology.class.toLowerCase()} sections available to the generator. Each {terminology.class.toLowerCase()} carries its own&nbsp;
            {terminology.teacher.toLowerCase()} and capacity, and can be used for {terminology.student.toLowerCase()}-level fixed overrides.
          </p>
        </div>

        <div className="hero-actions">
          <div className="roster-stat-block">
            <span className="metric-label">{terminology.classes}</span>
            <strong>{classesWorkspace.summary.totalClasses}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Seats</span>
            <strong>{classesWorkspace.summary.totalSeats}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">{terminology.students}</span>
            <strong>{classesWorkspace.summary.activeStudents}</strong>
          </div>
        </div>
        {canWrite ? (
          <div className="roster-action-bar">
            <button
              type="button"
              className="primary-button compact-button"
              onClick={handleCreateClick}
              disabled={isSaving}
            >
              + Add {terminology.class.toLowerCase()}
            </button>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">{terminology.class} catalog</span>
            <h2>Sections</h2>
          </div>
          <span className="toolbar-meta">{filteredClasses.length} visible {terminology.classes.toLowerCase()}</span>
        </div>

        <div className="table-wrap roster-list-wrap">
          <table className="data-grid classes-grid-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Grade</th>
                <th>{terminology.teacher}</th>
                <th>Capacity</th>
                <th>Assigned</th>
                <th>Locked</th>
              </tr>
            </thead>
            <tbody>
              {filteredClasses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-grid-cell">
                    {searchTerm.trim() ? `No ${terminology.classes.toLowerCase()} match the current search.` : `No ${terminology.classes.toLowerCase()} have been defined yet.`}
                  </td>
                </tr>
              ) : filteredClasses.map((schoolClass) => (
                <tr
                  key={schoolClass.id}
                  className={canWrite ? 'clickable-row' : ''}
                  onClick={() => {
                    if (!canWrite) return;
                    setSelectedClassId(schoolClass.id);
                    setDraft(buildClassDraft(schoolClass));
                    setIsEditDialogOpen(true);
                  }}
                >
                  <td className="class-code-cell">{schoolClass.name}</td>
                  <td>
                    <div className="list-cell">
                      <strong>{schoolClass.displayName}</strong>
                      <span style={schoolClass.seatsRemaining < 0 ? { color: 'var(--danger, #e53e3e)' } : undefined}>
                        {schoolClass.seatsRemaining < 0
                          ? `Over by ${Math.abs(schoolClass.seatsRemaining)} seat${Math.abs(schoolClass.seatsRemaining) === 1 ? '' : 's'}`
                          : `${schoolClass.seatsRemaining} seat${schoolClass.seatsRemaining === 1 ? '' : 's'} remaining`}
                      </span>
                    </div>
                  </td>
                  <td>{schoolClass.gradeLevel}</td>
                  <td>{schoolClass.teacherName}</td>
                  <td>{schoolClass.capacity}</td>
                  <td>{schoolClass.assignedStudents}</td>
                  <td>{schoolClass.fixedStudents}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {isEditDialogOpen && draft && selectedClass ? (
        <div className="workspace-dialog-backdrop" onClick={() => !isSaving && setIsEditDialogOpen(false)}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-class-title"
            onClick={(event) => event.stopPropagation()}
            style={{ width: 'min(560px, calc(100vw - 48px))' }}
          >
            <div className="panel-head compact" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span className="panel-kicker">{terminology.class} editor</span>
                <h2 id="edit-class-title">{editorTitle}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={() => !isSaving && setIsEditDialogOpen(false)}
                aria-label="Close class editor"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleSave}>
              <div className="classes-form-grid">
                <label className="auth-field">
                  <span>{terminology.class} code</span>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(event) => handleDraftChange('name', event.target.value)}
                    placeholder="5C"
                    autoFocus
                  />
                </label>

                <label className="auth-field">
                  <span>{terminology.class} name</span>
                  <input
                    type="text"
                    value={draft.displayName}
                    onChange={(event) => handleDraftChange('displayName', event.target.value)}
                    placeholder="Rivera"
                  />
                </label>

                <label className="auth-field">
                  <span>Grade level</span>
                  <CustomSelect
                    value={draft.gradeLevel}
                    options={[
                      { value: '', label: '— Select grade —' },
                      ...(classesWorkspace.grades ?? []).map((grade) => ({
                        value: grade.gradeLevel,
                        label: grade.gradeLevel
                      }))
                    ]}
                    onChange={(newValue) => handleDraftChange('gradeLevel', newValue)}
                  />
                </label>

                <label className="auth-field">
                  <span>{terminology.teacher}</span>
                  <input
                    type="text"
                    value={draft.teacherName}
                    onChange={(event) => handleDraftChange('teacherName', event.target.value)}
                    placeholder="Ms. Rivera"
                  />
                </label>

                <label className="auth-field">
                  <span>Capacity</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={draft.capacity}
                    onChange={(event) => handleDraftChange('capacity', event.target.value)}
                  />
                </label>
              </div>

              <div className="roster-related-grid classes-editor-metrics">
                <div className="mini-panel">
                  <span className="mini-label">Total {terminology.students.toLowerCase()}</span>
                  <strong>{classesWorkspace.summary.activeStudents}</strong>
                  <p>{classesWorkspace.summary.totalClasses} {terminology.classes.toLowerCase()} currently defined</p>
                </div>
                <div className="mini-panel">
                  <span className="mini-label">Current section load</span>
                  <strong>{selectedClass?.assignedStudents ?? 0}</strong>
                  <p>{selectedClass?.fixedStudents ?? 0} locked {terminology.student.toLowerCase()} overrides</p>
                </div>
              </div>

              <div className="roster-form-actions">
                {canWrite ? (
                  <button
                    type="button"
                    className="utility-button danger-text"
                    onClick={handleOpenDeleteDialog}
                    disabled={isSaving}
                  >
                    Delete
                  </button>
                ) : null}
                <button type="button" className="utility-button" onClick={handleReset} disabled={isSaving}>
                  Reset
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? `Saving ${terminology.class.toLowerCase()}` : `Save ${terminology.class.toLowerCase()}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isCreateDialogOpen ? (
        <div className="classes-dialog-backdrop" onClick={handleCloseCreateDialog}>
          <div
            className="classes-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-class-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">New {terminology.class.toLowerCase()}</span>
                <h2 id="create-class-title">Add {terminology.class.toLowerCase()} definition</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseCreateDialog}
                aria-label="Close add class dialog"
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleCreateSubmit}>
              <div className="classes-form-grid">
                <label className="auth-field">
                  <span>{terminology.class} code</span>
                  <input
                    type="text"
                    value={createDraft?.name ?? ''}
                    onChange={(event) => handleCreateDraftChange('name', event.target.value)}
                    placeholder="5C"
                    autoFocus
                  />
                </label>

                <label className="auth-field">
                  <span>{terminology.class} name</span>
                  <input
                    type="text"
                    value={createDraft?.displayName ?? ''}
                    onChange={(event) => handleCreateDraftChange('displayName', event.target.value)}
                    placeholder="Rivera"
                  />
                </label>

                <label className="auth-field">
                  <span>Grade level</span>
                  <CustomSelect
                    value={createDraft?.gradeLevel ?? ''}
                    options={[
                      { value: '', label: '— Select grade —' },
                      ...(classesWorkspace.grades ?? []).map((grade) => ({
                        value: grade.gradeLevel,
                        label: grade.gradeLevel
                      }))
                    ]}
                    onChange={(newValue) => handleCreateDraftChange('gradeLevel', newValue)}
                  />
                </label>

                <label className="auth-field">
                  <span>{terminology.teacher}</span>
                  <input
                    type="text"
                    value={createDraft?.teacherName ?? ''}
                    onChange={(event) => handleCreateDraftChange('teacherName', event.target.value)}
                    placeholder="Ms. Rivera"
                  />
                </label>

                <label className="auth-field">
                  <span>Capacity</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={createDraft?.capacity ?? 25}
                    onChange={(event) => handleCreateDraftChange('capacity', event.target.value)}
                  />
                </label>
              </div>

              <div className="roster-related-grid classes-editor-metrics">
                <div className="mini-panel">
                  <span className="mini-label">Total {terminology.students.toLowerCase()}</span>
                  <strong>{classesWorkspace.summary.activeStudents}</strong>
                  <p>{classesWorkspace.summary.totalClasses} {terminology.classes.toLowerCase()} currently defined</p>
                </div>
                <div className="mini-panel">
                  <span className="mini-label">Projected seats</span>
                  <strong>{Number(createDraft?.capacity ?? 0)}</strong>
                  <p>New section</p>
                </div>
              </div>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleResetCreateDialog}
                  disabled={isSaving}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseCreateDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? `Saving ${terminology.class.toLowerCase()}` : `Create ${terminology.class.toLowerCase()}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isDeleteDialogOpen && selectedClass ? (
        <div className="workspace-dialog-backdrop" onClick={handleCloseDeleteDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-class-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Confirm deletion</span>
                <h2 id="delete-class-title">Delete {selectedClass.displayName}?</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseDeleteDialog}
                aria-label="Close delete confirmation"
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {deleteError ? <div className="auth-error">{deleteError}</div> : null}

            {lockedStudentsForSelected.length > 0 ? (
              <div className="delete-blocked-notice">
                <p>
                  <strong>Cannot delete this {terminology.class.toLowerCase()}.</strong> The following {lockedStudentsForSelected.length === 1 ? `${terminology.student.toLowerCase()} is` : `${terminology.students.toLowerCase()} are`} locked to {selectedClass.displayName} as an override:
                </p>
                <ul className="delete-blocked-list">
                  {lockedStudentsForSelected.map((name, index) => (
                    <li key={index}>{name}</li>
                  ))}
                </ul>
                <p className="editor-note">
                  Remove their {terminology.class.toLowerCase()} overrides in the {terminology.students} view before deleting this {terminology.class.toLowerCase()}.
                </p>
                <div className="roster-form-actions">
                  <button
                    type="button"
                    className="utility-button"
                    onClick={handleCloseDeleteDialog}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="delete-confirm-body">
                <p>
                  This will permanently remove <strong>{selectedClass.displayName}</strong> ({selectedClass.name}) and its {selectedClass.capacity}-seat capacity from this project. {terminology.students} currently assigned to this {terminology.class.toLowerCase()} by the solver will be reassigned on the next run.
                </p>

                <div className="roster-form-actions">
                  <button
                    type="button"
                    className="utility-button"
                    onClick={handleCloseDeleteDialog}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button danger-button"
                    onClick={handleConfirmDelete}
                    disabled={isSaving}
                  >
                    {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                    {isSaving ? 'Deleting' : `Delete ${terminology.class.toLowerCase()}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildClassDraft(schoolClass) {
  return {
    name: schoolClass.name,
    displayName: schoolClass.displayName,
    gradeLevel: schoolClass.gradeLevel ?? '',
    teacherName: schoolClass.teacherName,
    capacity: schoolClass.capacity
  };
}

function createEmptyDraft(gradeLevel) {
  return {
    name: '',
    displayName: '',
    gradeLevel: gradeLevel ?? '',
    teacherName: '',
    capacity: 25
  };
}
